import { useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  useNotify,
  useRecordContext,
  useRefresh,
  useRedirect,
  type RaRecord,
} from 'react-admin';
import { SectionCard } from '../shared/Panel';
import { toDateTimeLocalValue } from '../shared/clientTime';
import { PreOrderForm } from './PreOrderForm';
import type { OpenQuestion } from '../shared/requiredFields';
import {
  updatePreOrder,
  acceptPreOrder,
  toErrorMessage,
  type PreOrderInput,
} from './preOrderApi';
import { missingFieldLabel } from './preOrderReadiness';
import { differs, useDiscardGuard } from '../shared/DiscardChangesDialog';
import { useFeature } from '../admin/featureFlags/FeatureGate';
import { FEATURE_PRE_ORDER_COPY, copyPath } from './preOrderCopyApi';
import { FEATURE_PRE_ORDER_CONTRACTOR, type ContractorRef } from './preOrderContractorApi';
import { AgreementSourceFields, agreementSourceReady } from './AgreementSource';

/**
 * Akcje na prezleceniu: poprawianie danych i akceptacja do zlecenia.
 *
 * ## Co tu było wcześniej
 *
 * Makieta — dwa przyciski, które wyświetlały powiadomienie. Prezlecenia nie
 * dało się ani utworzyć, ani poprawić, ani zaakceptować; cała oś zleceń była
 * ekranami do odczytu nad tabelami, które nie miały jak się zapełnić.
 *
 * ## Akceptacja jest jawną decyzją
 *
 * Z niej powstaje **zlecenie z naszym numerem** i od niej zaczyna się droga
 * do VIA. CLAUDE.md: system przygotowuje, człowiek zatwierdza. Dlatego
 * akceptacja jest osobnym przyciskiem, a nie skutkiem zapisania formularza.
 *
 * ## Braki pokazujemy przed akceptacją, nie po odmowie
 *
 * Serwer i tak odmówi niekompletnego prezlecenia, ale operator ma to wiedzieć
 * **patrząc na ekran**, a nie po kliknięciu. Lista braków przychodzi
 * w `missingFields` i jest liczona przy każdym zapisie.
 */

/** Data z API → pole formularza w czasie polskim (`shared/clientTime.ts`). */
const toDateTimeInputValue = (v: unknown): string | null =>
  typeof v === 'string' ? toDateTimeLocalValue(v) : null;

function fromRecord(r: RaRecord, contractorOn = false): PreOrderInput {
  // Kontrahent z kartoteki (fala B, 21.10.2026) — tylko z przełącznikiem;
  // bez niego formularz go nie zna i nie wysyła.
  const contractor = (r.contractor as ContractorRef | null | undefined) ?? null;
  return {
    ...(contractorOn ? { contractor, contractorId: contractor?.id ?? null } : {}),
    companyId: (r.companyId as number | null) ?? null,
    clientName: (r.clientName as string | null) ?? null,
    // Bez tych pól „Popraw dane” kasowałby numer i typ (16.10.2026).
    customerOrderNumber: (r.customerOrderNumber as string | null) ?? null,
    relation: (r.relation as string | null) ?? null,
    loadingCity: (r.loadingCity as string | null) ?? null,
    unloadingCity: (r.unloadingCity as string | null) ?? null,
    // Bez tych pól „Popraw dane” wysyłałby je puste i kasował wpisany kraj i kod.
    loadingCountry: (r.loadingCountry as string | null) ?? null,
    loadingPostalCode: (r.loadingPostalCode as string | null) ?? null,
    unloadingCountry: (r.unloadingCountry as string | null) ?? null,
    unloadingPostalCode: (r.unloadingPostalCode as string | null) ?? null,
    loadingDate: toDateTimeInputValue(r.loadingDate),
    unloadingDate: toDateTimeInputValue(r.unloadingDate),
    requiredVehicleType: (r.requiredVehicleType as string | null) ?? null,
    cargo: (r.cargo as string | null) ?? null,
    weight: (r.weight as number | null) ?? null,
    pallets: (r.pallets as number | null) ?? null,
    price: (r.price as number | null) ?? null,
    currency: (r.currency as string | null) ?? null,
    responsibleDept: (r.responsibleDept as string | null) ?? null,
  };
}

/**
 * `showReadiness` — braki i akceptację pokazuje ten panel albo — na karcie
 * prezlecenia, od 07.10.2026 — wspólna karta „Kompletność i akceptacja”
 * widoczna dla każdego (`PreOrderShow`). Wtedy tu ich nie powtarzamy.
 */
export const PreOrderActions = ({
  showReadiness = true,
  lockedReason = null,
  openQuestion = null,
}: {
  showReadiness?: boolean;
  /**
   * Obieg CS → Administracja (fala B, 21.10.2026): rekord jest u drugiego
   * działu — edycja i akceptacja wyłączone z powodem (API i tak odmówi 409).
   */
  lockedReason?: string | null;
  /** Otwarte pytanie Administracji przy polu (24.10.2026, `intakeQuestionField`). */
  openQuestion?: OpenQuestion | null;
} = {}) => {
  const record = useRecordContext<RaRecord>();
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();

  const [draft, setDraft] = useState<PreOrderInput | null>(null);
  // Dane z chwili otwarcia edycji — od nich liczymy, czy „Zamknij edycję”
  // porzuciłoby coś, czego nikt nie zapisał.
  const [opened, setOpened] = useState<PreOrderInput | null>(null);
  const [busy, setBusy] = useState(false);
  const { guard, dialog } = useDiscardGuard(draft !== null && differs(draft, opened));
  // Kopia z porównaniem (makieta back office v2, 20.10.2026, `preOrderCopy`).
  const copyOn = useFeature(FEATURE_PRE_ORDER_COPY);
  const contractorOn = useFeature(FEATURE_PRE_ORDER_CONTRACTOR);

  if (!record) return null;

  const missing = (record.missingFields as string[] | undefined) ?? [];
  const accepted = record.acceptedAt != null;

  const run = async (action: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await action();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const save = () =>
    void run(async () => {
      await updatePreOrder(record.id, draft ?? {});
      notify('Prezlecenie zapisane', { type: 'success' });
      setDraft(null);
      refresh();
    });

  const accept = () =>
    void run(async () => {
      const z = await acceptPreOrder(record.id);
      notify(`Powstało zlecenie ${z.orderNumber ?? `#${z.id}`}`, {
        type: 'success',
      });
      redirect('show', 'orders', z.id);
    });

  return (
    <SectionCard title="Akcje operatora">
      {showReadiness && accepted && (
        <Alert severity="success" sx={{ mb: 1 }}>
          Prezlecenie zaakceptowane
          {record.acceptedBy ? ` przez ${String(record.acceptedBy)}` : ''}.
          Dalsze poprawki robi się na zleceniu, które z niego powstało —
          inaczej nie wiadomo, co właściwie zostało przyjęte.
        </Alert>
      )}

      {showReadiness && !accepted && missing.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Do akceptacji brakuje: {missing.map(missingFieldLabel).join(', ')}
        </Alert>
      )}

      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }} useFlexGap>
        <Button
          size="small"
          variant="contained"
          color="success"
          startIcon={<CheckIcon />}
          // Akceptacja przy brakach i tak zostałaby odrzucona przez serwer —
          // ale operator ma zobaczyć powód, zanim kliknie.
          disabled={busy || accepted || missing.length > 0 || lockedReason !== null}
          onClick={accept}
        >
          Akceptuj i utwórz zlecenie
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<EditIcon />}
          disabled={busy || accepted || lockedReason !== null}
          onClick={() => {
            if (draft === null) {
              const initial = fromRecord(record, contractorOn);
              setDraft(initial);
              setOpened(initial);
            } else {
              guard(() => setDraft(null));
            }
          }}
        >
          {draft === null ? 'Popraw dane' : 'Zamknij edycję'}
        </Button>
        {/* Kopia także zaakceptowanego — to wzór dla nowego zlecenia, nie jego zmiana. */}
        {copyOn ? (
          <Button
            size="small"
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            disabled={busy || draft !== null}
            onClick={() => redirect(copyPath(record.id))}
            data-testid="adm-kopia-utworz"
          >
            Kopiuj
          </Button>
        ) : null}
        {busy && <CircularProgress size={18} sx={{ alignSelf: 'center' }} />}
      </Stack>
      {lockedReason ? (
        <Typography variant="caption" color="text.secondary" data-testid="pre-order-locked" sx={{ display: 'block', mt: 0.5 }}>
          {lockedReason}
        </Typography>
      ) : null}

      {draft !== null && (
        <Stack spacing={1} sx={{ mt: 1.5 }}>
          <PreOrderForm
            values={draft}
            locked={busy}
            openQuestion={openQuestion}
            onChange={(field, v) => setDraft((d) => ({ ...(d ?? {}), [field]: v }))}
            onContractorChange={(c) =>
              setDraft((d) => ({ ...(d ?? {}), contractor: c, contractorId: c?.id ?? null }))
            }
          />
          {/* Źródło ustalenia (21.10.2026, `preOrderAgreementSource`) — do historii zmienionych pól. */}
          <AgreementSourceFields
            value={draft.agreementSource}
            disabled={busy}
            preOrderId={record.id as number}
            onChange={(s) => setDraft((d) => ({ ...(d ?? {}), agreementSource: s }))}
          />
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Button
              size="small"
              variant="contained"
              disabled={busy || !agreementSourceReady(draft.agreementSource)}
              onClick={save}
            >
              Zapisz zmiany
            </Button>
            <Chip
              size="small"
              variant="outlined"
              label="każda zmiana trafia do historii"
            />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Niekompletne prezlecenie wolno zapisać — komplet jest potrzebny
            dopiero do akceptacji.
          </Typography>
        </Stack>
      )}
      {dialog}
    </SectionCard>
  );
};
